import { useApp } from "components/AppContext";
import { useBranchAnalytics } from "components/BranchIOAnalytics";
import CookieBanner from "components/CookieBanner";
import GridOverlay from "components/GridOverlay";
import {
  useAppStateRefresh,
  useIntercomLauncherDisplay,
} from "components/hooks";
import NProgress from "components/NProgress";
import { bindInstallPrompt } from "lib/app";
import type { Metadata } from "lib/metadata";
import { defaultDescription, defaultRobot, defaultTitle } from "lib/metadata";
import Head from "next/head";
import Router from "next/router";
import { FunctionComponent } from "react";
import { FontsCss } from "../../public/fonts";
import { GlobalCss } from "../css/Global";
import { SanitizeCss } from "../css/Sanitize";
import OpenGraphImage from "./penfold-og-image.jpg";

Router.events.on("routeChangeComplete", (url) => {
  global.analytics.page(url);
});

if (process.browser) {
  bindInstallPrompt();
}

// FYI - Default values here are those of the homepage
// TODO - we may want to only render the twitter meta
// on certain pages, and find a viable way to
// determine this logic.
const metaDefaults = {
  title: defaultTitle,
  robot: defaultRobot,
  description: defaultDescription,
  ogTitle: defaultTitle,
  ogDescription: defaultDescription,
  ogImage: `https://getpenfold.com${OpenGraphImage}`,
  ogType: "website",
  ogAuthor: null,
  ogPublishedTime: null,
  twitterTitle: defaultTitle,
  twitterDescription: defaultDescription,
  twitterImage: `https://getpenfold.com${OpenGraphImage}`,
  twitterSite: "@getpenfold",
  twitterCreator: "@pete_hykin",
};

const viewportContent = (isApp) =>
  `initial-scale=1.0, width=device-width${
    isApp
      ? ", viewport-fit=cover, minimum-scale=1.0, maximum-scale=1, user-scalable=no"
      : ""
  }`;

interface PageProps {
  meta?: Partial<Metadata>;
  background?: string;
  showIntercom: boolean;
  bottomSafeArea?: boolean;
}
export const Page: FunctionComponent<PageProps> = ({
  children,
  meta,
  background = null,
  showIntercom,
  bottomSafeArea = true,
}) => {
  const { isApp } = useApp();

  useBranchAnalytics();

  useAppStateRefresh();

  useIntercomLauncherDisplay(showIntercom);

  const mergedData = { ...metaDefaults, ...meta };
  const {
    title,
    description,
    ogTitle,
    ogUrl,
    ogDescription,
    ogImage,
    ogType,
    ogAuthor,
    ogPublishedTime,
    twitterTitle,
    twitterDescription,
    twitterImage,
    twitterSite,
    twitterCreator,
    robot,
    canonicalLink,
  } = mergedData;

  return (
    <>
      <Head>
        {/* Basic meta */}
        <title>{title} - Penfold</title>
        <meta name="robots" content={robot} />
        <meta name="description" content={description} />
        <meta name="viewport" content={viewportContent(isApp)} />

        {/* Social meta */}
        <meta property="og:title" content={ogTitle || title} />
        <meta property="og:url" content={ogUrl} />
        <meta
          property="og:description"
          content={ogDescription || description}
        />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content={ogType} />

        {ogAuthor && <meta property="og:article:author" content={ogAuthor} />}
        {ogPublishedTime && (
          <meta
            property="og:article:published_time"
            content={ogPublishedTime}
          />
        )}

        {/* Twitter Card*/}
        <meta name="twitter:title" content={twitterTitle || title} />
        <meta
          name="twitter:description"
          content={twitterDescription || description}
        />
        <meta name="twitter:image" content={twitterImage || ogImage} />
        <meta name="twitter:site" content={twitterSite || ogUrl} />
        <meta name="twitter:creator" content={twitterCreator} />

        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/assets/images/fav/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/assets/images/fav/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/assets/images/fav/favicon-16x16.png"
        />
        <link rel="manifest" href="/assets/images/fav/site.webmanifest" />
        <link
          rel="mask-icon"
          href="/assets/images/fav/safari-pinned-tab.svg"
          color="#5bbad5"
        />
        <link rel="shortcut icon" href="/assets/images/fav/favicon.ico" />
        {canonicalLink && (
          <link
            rel="canonical"
            href={`https://getpenfold.com${canonicalLink}`}
          />
        )}
        <meta name="msapplication-TileColor" content="#2d89ef" />
        <meta
          name="msapplication-config"
          content="/assets/images/fav/browserconfig.xml"
        />
        <meta name="theme-color" content="#ffffff" />

        <script
          dangerouslySetInnerHTML={{
            __html: `window.intercomSettings = {"hide_default_launcher": true};`,
          }}
        />
      </Head>

      <SanitizeCss />
      <GlobalCss {...{ background, bottomSafeArea, isApp }} />
      <FontsCss />
      <NProgress />

      {children}

      <GridOverlay />
      <CookieBanner />
    </>
  );
};
